import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Mic, X } from "lucide-react";
import VoiceNoteRecorder from "@/components/VoiceNoteRecorder";

/**
 * FloatingMicButton. always-on quick-record affordance for logged-in users.
 *
 * Behaviour:
 *   - Hidden when logged out, on auth/billing pages, or on the /brand/phone
 *     headless capture route.
 *   - When clicked: if the user is already inside a case (URL /cases/:id),
 *     open the recorder against that case. Otherwise show a case picker.
 *   - When the user has zero cases, prompt to create one first.
 */
const HIDDEN_ROUTES = [
  /^\/login/, /^\/register/, /^\/verify-email/, /^\/brand\//, /^\/billing\//, /^\/admin\//,
  // Marketing / public pages — a floating mic on a "should I subscribe?"
  // page is noisy. Logged-in users can still tap it from /dashboard or
  // inside any case page.
  /^\/pricing/, /^\/features/, /^\/q($|\/)/, /^\/founder/, /^\/privacy/, /^\/terms/, /^\/disclaimer/,
  // Public read-only share pages (voice notes, comparisons, verify-hash)
  /^\/v\//, /^\/c\//, /^\/verify\//,
];

export default function FloatingMicButton() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const [open, setOpen] = useState(false);
  const [picker, setPicker] = useState(false);
  const [cases, setCases] = useState([]);
  const [activeCaseId, setActiveCaseId] = useState(null);

  const inCaseRoute = /^\/cases\/[^/]+/.test(location.pathname);
  const caseIdFromUrl = inCaseRoute ? location.pathname.split("/")[2] : null;

  if (!user) return null;
  if (HIDDEN_ROUTES.some((re) => re.test(location.pathname))) return null;

  const handleClick = async () => {
    if (caseIdFromUrl) {
      setActiveCaseId(caseIdFromUrl);
      setOpen(true);
      return;
    }
    // Need to pick a case first
    try {
      const { data } = await api.get("/cases");
      if (!data || data.length === 0) {
        // No cases yet — push them to create one
        navigate("/dashboard");
        return;
      }
      if (data.length === 1) {
        setActiveCaseId(data[0].id);
        setOpen(true);
        return;
      }
      setCases(data);
      setPicker(true);
    } catch (e) {
      // Quiet fail — most likely auth issue or offline
    }
  };

  const onCasePick = (id) => {
    setActiveCaseId(id);
    setPicker(false);
    setOpen(true);
  };

  return (
    <>
      <button
        onClick={handleClick}
        className="fixed bottom-20 md:bottom-6 right-4 md:right-6 z-40 bg-[#B35D4C] hover:bg-[#9c4d3e] text-white rounded-full p-4 shadow-lg transition-transform active:scale-95"
        aria-label="Record voice note"
        title="Record voice note"
        data-testid="floating-mic-btn"
      >
        <Mic size={22} strokeWidth={1.8} />
      </button>

      {picker && (
        <div
          className="fixed inset-0 z-[55] bg-black/40 flex items-center justify-center p-4"
          onClick={() => setPicker(false)}
          data-testid="voice-case-picker"
        >
          <div
            className="bg-[#FAF9F6] border border-[#E5E4DE] rounded-sm max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-[#E5E4DE]">
              <h3 className="font-serif text-lg text-[#0D2B52]">Pick a case</h3>
              <button onClick={() => setPicker(false)} className="btn-ghost p-1" aria-label="Close">
                <X size={16} strokeWidth={1.5} />
              </button>
            </div>
            <div className="divide-y divide-[#EFEDE5] max-h-[60vh] overflow-y-auto">
              {cases.map((c) => (
                <button
                  key={c.id}
                  onClick={() => onCasePick(c.id)}
                  className="w-full text-left px-4 py-3 hover:bg-[#EFEDE5]/50"
                  data-testid={`voice-case-pick-${c.id}`}
                >
                  <div className="font-serif text-sm text-[#0D2B52]">{c.title}</div>
                  <div className="text-xs text-[#6B7280] mt-0.5">{c.address}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {open && activeCaseId && (
        <VoiceNoteRecorder
          caseId={activeCaseId}
          onUploaded={() => {
            // Refresh case detail page if we're on it
            window.dispatchEvent(new CustomEvent("tt:voice-note-added", { detail: { caseId: activeCaseId } }));
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
